import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { areaEnableDisable } from '@/api/domains/organization/areas';
import { useUI } from '@/hooks/ui/useUI';


interface UseAreaEnableDisableResult {
  enableArea: (areaId: number) => Promise<boolean>;
  disableArea: (areaId: number) => Promise<boolean>;
  isEnabling: boolean;
  isDisabling: boolean;
  enablingIds: Set<number>;
  disablingIds: Set<number>;
}

/**
 * Hook for enabling/disabling areas
 * Returns separate loading states for enable and disable operations
 */
export function useAreaEnableDisable(): UseAreaEnableDisableResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [enablingIds, setEnablingIds] = useState<Set<number>>(new Set());
  const [disablingIds, setDisablingIds] = useState<Set<number>>(new Set());

  const enableArea = useCallback(async (areaId: number): Promise<boolean> => {
    setIsEnabling(true);
    setEnablingIds((previous) => new Set(previous).add(areaId));

    const response = await areaEnableDisable.enable(areaId);

    setEnablingIds((previous) => {
      const next = new Set(previous);
      next.delete(areaId);
      return next;
    });
    setIsEnabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.areaEnabled'), 5000);
      return true;
    }

    showApiError(response, translate('organization:messages.areaEnableError'));
    return false;
  }, [showNotification, showApiError, translate]);

  const disableArea = useCallback(async (areaId: number): Promise<boolean> => {
    setIsDisabling(true);
    setDisablingIds((previous) => new Set(previous).add(areaId));

    const response = await areaEnableDisable.disable(areaId);

    setDisablingIds((previous) => {
      const next = new Set(previous);
      next.delete(areaId);
      return next;
    });
    setIsDisabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.areaDisabled'), 5000);
      return true;
    }

    showApiError(response, translate('organization:messages.areaDisableError'));
    return false;
  }, [showNotification, showApiError, translate]);

  return {
    enableArea,
    disableArea,
    isEnabling,
    isDisabling,
    enablingIds,
    disablingIds,
  };
}
